import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { AlertCircle, TrendingUp, TrendingDown } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { Input } from './ui/input';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TEAL = '#2EB4AC';
const TEAL_DARK = '#259993';

// "Stale" = the singleton hasn't been updated since last Monday 00:00 local
const lastMondayMidnight = () => {
  const now = new Date();
  const daysSinceMon = (now.getDay() + 6) % 7; // Mon=0 ... Sun=6
  const d = new Date(now);
  d.setDate(now.getDate() - daysSinceMon);
  d.setHours(0, 0, 0, 0);
  return d;
};

const compactNumber = (n) => {
  if (n == null || isNaN(n)) return '0';
  if (n >= 1_00_00_000) return `${(n / 1_00_00_000).toFixed(1)}cr`;
  if (n >= 1_00_000) return `${(n / 1_00_000).toFixed(1)}L`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return `${Math.round(n)}`;
};

const formatINR = (n) =>
  `₹${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(
    Math.round(n || 0)
  )}`;

const localPart = (email) => (email ? email.split('@')[0] : 'someone');

const StaleDot = ({ size = 'small' }) => {
  const s = size === 'small' ? 'h-2.5 w-2.5' : 'h-3 w-3';
  return (
    <span className="absolute -top-0.5 -right-0.5 flex">
      <span className={`absolute inline-flex rounded-full bg-red-400 opacity-75 animate-ping ${s}`} />
      <span className={`relative inline-flex rounded-full bg-red-500 border-2 border-white ${s}`} />
    </span>
  );
};

const Sparkline = ({ data, width = 56, height = 18 }) => {
  if (!data || data.length < 2) return null;
  const values = data.map((d) => d.credits);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const stepX = width / (data.length - 1);
  const points = data
    .map((d, i) => {
      const x = i * stepX;
      const y = height - ((d.credits - min) / range) * height;
      return `${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(' ');
  const lastX = (data.length - 1) * stepX;
  const lastY = height - ((values[values.length - 1] - min) / range) * height;
  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className="overflow-visible"
      aria-hidden="true"
    >
      <polyline
        points={points}
        fill="none"
        stroke={TEAL}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx={lastX} cy={lastY} r="2" fill={TEAL} />
    </svg>
  );
};

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload;
  return (
    <div
      className="bg-white border border-slate-200 px-3 py-2 text-xs"
      style={{ borderRadius: 12 }}
    >
      <div className="font-semibold text-slate-900 mb-0.5">
        {format(new Date(label), 'd MMM yyyy')}
      </div>
      <div className="flex items-center gap-2 text-slate-600">
        <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: TEAL }} />
        <span>{compactNumber(p.credits)} credits · {formatINR(p.cost_inr)}</span>
      </div>
    </div>
  );
};

const CostTrackerWidget = () => {
  const [state, setState] = useState(null);
  const [history, setHistory] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creditsInput, setCreditsInput] = useState('');
  const [rateInput, setRateInput] = useState('');
  const [saving, setSaving] = useState(false);

  const loadAll = async () => {
    try {
      const [s, h] = await Promise.all([
        axios.get(`${API}/cost-tracker`),
        axios.get(`${API}/cost-tracker/history?weeks=12`),
      ]);
      setState(s.data);
      setHistory(Array.isArray(h.data?.history) ? h.data.history : []);
    } catch (e) {
      // silently swallow - widget should never break the app shell
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const stale =
    !state?.updated_at || new Date(state.updated_at) < lastMondayMidnight();

  const credits = state?.total_credits || 0;
  const rate = state?.cost_per_credit_inr ?? 13.7;
  const spent = credits * rate;

  const previewCredits = parseFloat(creditsInput);
  const previewRate = parseFloat(rateInput);
  const previewTotal =
    (isNaN(previewCredits) ? credits : previewCredits) *
    (isNaN(previewRate) ? rate : previewRate);

  const chartData = useMemo(
    () =>
      history.map((h) => ({
        ...h,
        date: h.week_start,
      })),
    [history]
  );

  const trendDelta = useMemo(() => {
    if (history.length < 2) return null;
    const last = history[history.length - 1].credits;
    const prev = history[history.length - 2].credits;
    const diff = last - prev;
    const pct = prev === 0 ? 100 : (diff / prev) * 100;
    return { diff, pct, up: diff > 0, flat: diff === 0 };
  }, [history]);

  const openDialog = () => {
    setCreditsInput(credits ? String(credits) : '');
    setRateInput(rate != null ? String(rate) : '13.70');
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const tc = parseFloat(creditsInput);
    const cpc = parseFloat(rateInput);
    if (isNaN(tc) || isNaN(cpc)) {
      toast.error('Enter valid numbers');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/cost-tracker`, {
        total_credits: tc,
        cost_per_credit_inr: cpc,
      });
      await loadAll();
      toast.success('Cost tracker updated');
      setDialogOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (!state) {
    return (
      <button
        type="button"
        className="relative h-9 w-9 rounded-full hover:bg-slate-100 transition-colors flex items-center justify-center"
        aria-label="Cost tracker loading"
        data-testid="cost-tracker-loading"
      >
        <img src="/icons/cost-rupee.png" alt="" className="h-6 w-6 opacity-50" />
      </button>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={openDialog}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openDialog();
          }
        }}
        className="relative h-9 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 hover:border-slate-300 shadow-sm cursor-pointer flex items-stretch overflow-hidden transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-1"
        title={stale ? "It's a new week — click to update credits" : 'Click to manage cost tracker'}
        aria-label="Open cost tracker"
        data-testid="cost-tracker-widget"
      >
        {/* Credits cell */}
        <div className="flex items-center gap-1.5 px-2.5 bg-slate-50/60">
          <img src="/icons/cost-rupee.png" alt="" className="h-3.5 w-3.5 opacity-90" />
          <div className="flex flex-col leading-none text-left">
            <span className="text-[8px] font-semibold uppercase text-slate-400 tracking-wider">Credits</span>
            <span className="text-[11px] font-bold text-slate-800 tabular-nums mt-0.5">
              {compactNumber(credits)}
            </span>
          </div>
        </div>

        <div className="w-px bg-slate-200" />

        {/* Spent cell */}
        <div className="flex items-center gap-2 px-2.5">
          <div className="flex flex-col leading-none text-left">
            <span className="text-[8px] font-semibold uppercase tracking-wider" style={{ color: TEAL }}>Spent</span>
            <span className="text-[11px] font-bold text-slate-900 tabular-nums mt-0.5">{formatINR(spent)}</span>
          </div>
          <div className="hidden lg:flex items-center"><Sparkline data={chartData} width={48} height={16} /></div>
        </div>

        {stale && <StaleDot size="small" />}
      </button>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="cost-tracker-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <img src="/icons/cost-rupee.png" alt="" className="h-5 w-5" />
              <span>App Development Cost Tracker</span>
            </DialogTitle>
          </DialogHeader>

          {stale && (
            <div className="flex gap-2 items-start bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-900">
              <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
              <p className="text-xs">
                It&rsquo;s a new week &mdash; please update the credits consumed since last
                Monday.
              </p>
            </div>
          )}

          {/* Trendline chart */}
          {history.length >= 2 ? (
            <div className="rounded-xl border border-slate-200 p-4">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h4 className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">
                    Week-on-week credits
                  </h4>
                  <p className="text-[11px] text-slate-400 mt-0.5">
                    Last {history.length} weeks &middot; Mondays
                  </p>
                </div>
                {trendDelta && !trendDelta.flat && (
                  <div
                    className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                      trendDelta.up
                        ? 'bg-red-50 text-red-700'
                        : 'bg-green-50 text-green-700'
                    }`}
                    data-testid="cost-tracker-trend-chip"
                  >
                    {trendDelta.up ? (
                      <TrendingUp size={12} />
                    ) : (
                      <TrendingDown size={12} />
                    )}
                    <span>
                      {trendDelta.up ? '+' : ''}
                      {compactNumber(Math.abs(trendDelta.diff))}
                      {' ('}
                      {trendDelta.up ? '+' : ''}
                      {trendDelta.pct.toFixed(0)}%{')'}
                    </span>
                  </div>
                )}
              </div>
              <div style={{ height: 176 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData} margin={{ top: 4, right: 8, left: -10, bottom: 0 }}>
                    <defs>
                      <linearGradient id="ctFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={TEAL} stopOpacity={0.35} />
                        <stop offset="100%" stopColor={TEAL} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="#E2E8F0"
                      horizontal
                      vertical={false}
                    />
                    <XAxis
                      dataKey="date"
                      tickFormatter={(d) => format(new Date(d), 'd MMM')}
                      tick={{ fill: '#94A3B8', fontSize: 10 }}
                      axisLine={{ stroke: '#E2E8F0' }}
                      tickLine={false}
                    />
                    <YAxis
                      tickFormatter={(v) => compactNumber(v)}
                      tick={{ fill: '#94A3B8', fontSize: 10 }}
                      axisLine={false}
                      tickLine={false}
                      width={36}
                    />
                    <Tooltip content={<ChartTooltip />} />
                    <Area
                      type="monotone"
                      dataKey="credits"
                      stroke={TEAL}
                      strokeWidth={2}
                      fill="url(#ctFill)"
                      dot={{ r: 3, fill: '#fff', stroke: TEAL, strokeWidth: 2 }}
                      activeDot={{ r: 5, fill: TEAL, stroke: '#fff', strokeWidth: 2 }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-slate-300 p-5 text-center bg-slate-50/40">
              <p className="text-xs text-slate-500">
                Save updates each week to build the trendline.
              </p>
            </div>
          )}

          {/* Inputs */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">
                Total credits consumed
              </label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={creditsInput}
                onChange={(e) => setCreditsInput(e.target.value)}
                placeholder="0"
                data-testid="cost-tracker-credits-input"
              />
            </div>
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">
                ₹ per credit
              </label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={rateInput}
                onChange={(e) => setRateInput(e.target.value)}
                placeholder="13.70"
                data-testid="cost-tracker-rate-input"
              />
              <p className="text-[10px] text-slate-400 mt-1">
                Default ₹13.70 — edit anytime as billing changes.
              </p>
            </div>
          </div>

          {/* Live preview */}
          <div className="rounded-lg bg-slate-50/60 border border-slate-200 p-3 flex items-center justify-between">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              Estimated cost so far
            </span>
            <span
              className="text-xl font-bold tabular-nums"
              style={{ color: TEAL }}
              data-testid="cost-tracker-live-preview"
            >
              {formatINR(previewTotal)}
            </span>
          </div>

          {state?.updated_at && (
            <p className="text-[11px] text-slate-400">
              Last updated {format(new Date(state.updated_at), 'PPp')} by{' '}
              {localPart(state.updated_by)}
            </p>
          )}

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setDialogOpen(false)}
              data-testid="cost-tracker-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="text-white"
              style={{ backgroundColor: TEAL }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = TEAL_DARK)}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = TEAL)}
              data-testid="cost-tracker-save"
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CostTrackerWidget;
